<template>
  <div ref="dateRangeCalendar" class="flex flex-col">
    <header
      class="relative flex items-center justify-center space-x-2 border-b border-bgr-300 p-3 md:p-4 max-md:bg-bgr-100"
    >
      <button
        v-for="(label, key) in TABS"
        :key="key"
        class="rounded mt-0.5 px-2 font-medium transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover focus:border-thm-hover"
        @click.stop="tab = key"
      >
        <div
          class="border-y border-transparent hover:text-thm-hover px-2 py-1"
          :class="{ 'border-b-thm text-thm': tab === key }"
        >
          {{ $t(`${trPrefixSearchDates}${label}`) }}
        </div>
      </button>

      <button
        class="absolute right-4 rounded-full p-1 transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover focus:border-thm-hover"
        @click="close"
      >
        <WebccIcon name="site/x" class="h-6 w-6 text-txt-weak" />
      </button>
    </header>

    <main class="flex flex-1 flex-col gap-4 overflow-y-auto py-6">
      <div v-if="tab === 'flexible'" class="flex flex-col gap-8">
        <div class="flex flex-col gap-4">
          <h2 class="text-center text-xl font-medium">
            {{ $t(`${trPrefixFlexibleDates}h_duration`) }}
          </h2>

          <div
            class="mx-auto flex w-fit max-w-[95%] flex-wrap items-center justify-center gap-4 sm:gap-6"
          >
            <WebccButton
              v-for="durationIt in DURATIONS_PRESET"
              :key="durationIt"
              class="w-[6.5rem] rounded py-2.5 font-medium focus:border-thm-hover focus:ring-thm-hover md:w-36 md:py-2"
              variant="white"
              outline
              :class="{ 'bg-bgr-200': duration === durationIt }"
              @click="selectDuration(durationIt)"
            >
              <span class="font-medium">{{ getDurationText(durationIt) }}</span>
            </WebccButton>
            <select
              :value="
                !duration || DURATIONS_PRESET.includes(duration) ? 0 : duration
              "
              class="w-[6.5rem] cursor-pointer rounded border border-bgr-300 bg-bgr py-2.5 pr-6 text-sm font-medium transition hover:border-bgr-400 hover:bg-bgr-200 focus:border-thm-hover focus:bg-bgr-200 focus:outline-none focus:ring-2 focus:ring-thm-hover focus:ring-offset-2 md:w-36 md:py-2 md:text-base"
              :class="{
                'bg-bgr-200': duration && !DURATIONS_PRESET.includes(duration),
              }"
              @input="
                selectDuration(+($event.target as HTMLInputElement).value)
              "
            >
              <option disabled hidden class="padding-x-4" selected value="0">
                {{ $t(`${trPrefixFlexibleDates}custom`) }}
              </option>
              <option
                v-for="i in DURATION_MAX"
                :key="i"
                class="padding-x-4 text-txt"
                :value="i"
              >
                {{ $t(`${trPrefixFlexibleDates}night_s`, {}, i) }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <h2 class="text-center text-xl font-medium">
            {{ $t(`${trPrefixFlexibleDates}h_range`) }}
          </h2>

          <div class="text-center text-txt-stronger">
            {{ $t(`${trPrefixFlexibleDates}tip_range`) }}
          </div>
        </div>
      </div>

      <WebccDateRangepicker
        :key="tab"
        v-model="range"
        :locale="$i18n.locale"
        :min-length="(tab === 'flexible' && duration) || DURATION_MIN"
        :max-length="tab === 'flexible' ? DURATION_MAX_FLEX : DURATION_MAX"
        @select-start="onStartDateSelect"
      />

      <div
        v-if="screen.isExtraSmall && tab === 'default'"
        class="px-4 w-full box-border text-center"
      >
        <WebccDateRangePlusMinusDays v-model="fuzziness" />
        <span class="font-semibold">{{ tripText }}</span>
      </div>
    </main>

    <footer
      class="flex w-full items-center justify-between border-t border-bgr-300 p-3 md:p-4 max-md:bg-bgr-100 md:static"
    >
      <WebccButton
        variant="link"
        class="underline !py-2.5 !px-3 !text-base focus:ring-thm-hover focus:border-thm-hover"
        :disabled="!valid"
        @click="reset"
      >
        {{ $t(`${trPrefixSearchDates}reset`) }}
      </WebccButton>
      <WebccDateRangePlusMinusDays
        v-if="!screen.isExtraSmall && tab === 'default'"
        v-model="fuzziness"
      />
      <WebccButton
        variant="theme"
        class="!py-2.5 !px-3 !text-base"
        :disabled="!valid"
        @click="apply"
      >
        {{ $t(`${trPrefixSearchDates}apply`) }}
      </WebccButton>
    </footer>
  </div>
</template>
<script setup lang="ts">
import { addDays } from 'date-fns'
import type { DatePeriod } from '~/utils/dates'
import { DURATION_MIN, DURATION_MAX, DURATION_MAX_FLEX } from '~/utils/duration'

const trPrefixSearchDates = 'www.components.elements.SearchDates.'
const trPrefixFlexibleDates = 'www.components.elements.FlexibleDates.'

const TABS = { default: 'calendar', flexible: 'flexible' } as const
const DURATIONS_PRESET = [7, 14]

defineExpose({ focusOnFirstDate, reset })

export type CalendarTab = 'default' | 'flexible'
export type CalendarEventState = (DatePeriod | object) & {
  tab: CalendarTab
}

const model = defineModel<DatePeriod | undefined>({ required: true })

const emit = defineEmits<{
  close: []
  tabChange: [tab: CalendarTab, state: CalendarEventState]
  startDateSelect: [startDate: Date | undefined, state: CalendarEventState]
  dateRangeChange: [range: DateRange, state: CalendarEventState]
  reset: [state: CalendarEventState]
  apply: [model: DatePeriod | undefined]
}>()

const { t } = useI18n()
const screen = useScreen()
const tracking = useTracking()

const dateRangeCalendar = ref<HTMLDivElement>()

const tab = ref<CalendarTab>(
  isFlexDatePeriod(model.value) ? 'flexible' : 'default',
)

const range = ref<{ start: Date | undefined; end: Date | undefined }>({
  start: model.value?.startDate,
  end: model.value?.endDate,
})

const duration = ref<number | undefined>(
  isFlexDatePeriod(model.value) ? model.value.duration : undefined,
)

watch(tab, (tabValue) => {
  if (tabValue !== 'flexible' && duration.value) return
  if (!isDateRangeValid(range.value)) return

  duration.value = calculateDuration(range.value.start, range.value.end)
})

const fuzziness = ref<number | undefined>(
  isFuzzyDatePeriod(model.value) ? model.value.fuzziness : undefined,
)

const datePeriod = computed<DatePeriod | undefined>(() => {
  const { start: startDate, end: endDate } = range.value
  if (!startDate || !endDate) return undefined
  if (tab.value === 'flexible') {
    return removeFalsy({
      startDate,
      endDate,
      duration: duration.value,
    })
  }
  return removeFalsy({
    startDate,
    endDate,
    fuzziness: fuzziness.value,
  })
})

const tripText = useDatesTripText(datePeriod)

watch(duration, function adjustEndDateOnDurationChange(newDuration) {
  const { start, end } = range.value
  if (!newDuration || !start || !end) return
  const calculatedDuration = calculateDuration(start, end)
  if (newDuration > calculatedDuration) {
    range.value = { start, end: addDays(start, newDuration) }
  }
})

const valid = computed(
  () =>
    isDateRangeValid(range.value) || (!range.value.start && !range.value.end),
)

function getDurationText(value?: number) {
  return getDatesDurationText(t, value)
}

onMounted(async () => {
  await nextTick()
  dateRangeCalendar.value?.setAttribute('tabindex', '-1')
})

function close() {
  emit('close')
}

function focusOnFirstDate() {
  dateRangeCalendar.value
    ?.querySelector(
      `.id-${toDate(range.value.start ?? new Date())} .vc-day-content` as 'div',
    )
    ?.focus({ preventScroll: true })
}

function selectDuration(newDuration: number) {
  tracking.handlers?.search.datesDurationSelectFlex(newDuration)
  tracking.handlers?.homepage.datesDurationSelect(newDuration)
  duration.value = newDuration
}

function reset() {
  range.value = { start: undefined, end: undefined }
  duration.value = undefined
  fuzziness.value = undefined
  emit('reset', getCalendarEventState())
}

function applyValue() {
  if (!valid.value) {
    return
  }
  const newValue = isValidDatePeriod(datePeriod.value)
    ? datePeriod.value
    : undefined

  emit('apply', newValue)
  model.value = newValue
}

watch([tab, range, duration, fuzziness], applyValue)

function apply() {
  applyValue()
  close()
}

watch(range, function onRangeChange(current) {
  emit('dateRangeChange', current, getCalendarEventState())
})

watch(tab, function onTabChange(val) {
  emit('tabChange', val, getCalendarEventState())
  window.sessionStorage.setItem('flexible-search', val ? 'true' : 'false')
})

function onStartDateSelect(value?: Date) {
  emit('startDateSelect', value, getCalendarEventState())
}

function getCalendarEventState(): CalendarEventState {
  return { ...(datePeriod.value || {}), tab: tab.value }
}
</script>
